/** @jsx jsx */
/**
 * Help-center side of the Hub Forge/Connect renderer integration.
 *
 * The platform-level packages (`@atlassian/hub-forge-extension`,
 * `@atlassian/hub-connect-extension`) ship the ADF schema, manifests and
 * empty-state UI for the four new editor elements (Forge header / footer,
 * Connect header / footer). They intentionally do NOT depend on any Forge
 * or Connect SDK — the host product is expected to inject a concrete
 * renderer via the `HubForge|ConnectRendererProvider` context.
 *
 * This module is that injection point for the Help Hub / Customer Service
 * Hub. It mirrors how the legacy help-center Forge/Connect customisation
 * elements render today (see
 * `help-center/packages/help-center/customisation/elements/forge`
 * and `…/elements/connect`):
 *  - same `<ElementRoot>` wrapper (margin/padding tokens, flexibility flag)
 *  - same pointer-events shield while editing (gated by `mode === EDITOR`)
 *  - same `<ConnectFragment>` fan-out for `headerAndSubheaderPanels`
 *  - same `trackError` analytics on invalid `elementType`s
 *
 * "Is Connect installed on the tenant?" / "Is Forge installed?" are
 * decided server-side: the BE only emits a `HubForgeExtension` /
 * `HubConnectExtension` ADF node when the corresponding app is available
 * for the slot. At render time the legacy `<ForgeHeaderModule>` /
 * `<ConnectFragment>` primitives perform a second, finer-grained check
 * (their internal Relay queries return empty when there are no matching
 * Forge/Connect modules, in which case they render nothing — matching the
 * legacy element behaviour).
 *
 * Mounted through `HubAppProps.productExtensionsDependenciesProvider` so
 * platform extension renderers can resolve Help Center's concrete Forge / Connect
 * primitives from both published renderer and editor extension roots.
 */

import React, { type ReactNode } from 'react';
import { jsx } from '@compiled/react';
import { css } from '@atlaskit/css';

import { IntlProvider } from 'react-intl';

import { connect } from 'react-redux';

import {
	HubConnectRendererProvider,
	type HubConnectRenderer,
	ConnectElementType,
	HubConnectEmptyState,
} from '@atlassian/hub-connect-extension';
import {
	HubForgeRendererProvider,
	type HubForgeRenderer,
	ForgeElementType,
	HubForgeEmptyState,
} from '@atlassian/hub-forge-extension';
import { CommonHubExtensionsDependenciesProvider } from '@atlassian/common-hub-extensions-dependencies-provider';

import { HELP_CENTER, ConnectFragment } from '@helpcenter/connect-fragment';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';

import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { ForgeFooterModule } from 'view/forge-ui/portal-footer';
import { ForgeHeaderModule } from 'view/forge-ui/portal-header';
import { useTFExtensionList } from 'view/common/forge-ui/use-tf-extension-list';
import { ExtensionMode } from '@atlassian/company-hub-common';
import {
	PORTAL_HEADER_MODULE,
	PORTAL_SUBHEADER_MODULE,
	PORTAL_FOOTER_MODULE,
} from '@helpcenter/forge-ui-utils/src/constants';
import { getConnectAddonPanels, isConnectFragmentsResolved } from 'state/selectors/connect-addon';
import type { ConnectFragmentLocation } from 'state/selectors/connect-addon';
import type { State } from 'state';

import { trackError } from '@atlassian/help-center-common-util/analytics';

// ---------------------------------------------------------------------------
// Forge bridge
// ---------------------------------------------------------------------------

const FORGE_PAGE = 'help_center' as const;
const forgeModuleProps = {
	page: FORGE_PAGE,
	noWrapper: true,
	allowCustomPadding: true,
	skipHelpCenterWrapper: true,
} as const;

/**
 * Header slot: renders the Forge header + subheader modules. In the editor,
 * once the trusted-fetch resolves with no installed apps for either module,
 * it shows the empty-state placeholder instead so authors know the slot is
 * empty. In the published view the modules self-render nothing when empty.
 */
const ForgeHeaderSlot: React.FC<{ isPageInEditMode: boolean }> = ({ isPageInEditMode }) => {
	const { extensions: headerExtensions, resolved: headerResolved } = useTFExtensionList({
		type: PORTAL_HEADER_MODULE,
		page: FORGE_PAGE,
	});
	const { extensions: subheaderExtensions, resolved: subheaderResolved } = useTFExtensionList({
		type: PORTAL_SUBHEADER_MODULE,
		page: FORGE_PAGE,
	});

	const isEmpty = (headerExtensions?.length ?? 0) === 0 && (subheaderExtensions?.length ?? 0) === 0;
	if (isPageInEditMode && headerResolved && subheaderResolved && isEmpty) {
		return <HubForgeEmptyState />;
	}

	return <ForgeHeaderModule {...forgeModuleProps} />;
};

/**
 * Footer slot: same behaviour as {@link ForgeHeaderSlot} for the single
 * footer module type.
 */
const ForgeFooterSlot: React.FC<{ isPageInEditMode: boolean }> = ({ isPageInEditMode }) => {
	const { extensions, resolved } = useTFExtensionList({
		type: PORTAL_FOOTER_MODULE,
		page: FORGE_PAGE,
	});

	if (isPageInEditMode && resolved && (extensions?.length ?? 0) === 0) {
		return <HubForgeEmptyState />;
	}

	return <ForgeFooterModule {...forgeModuleProps} />;
};

/**
 * Adapter from the platform-level Forge renderer signature to the legacy
 * help-center Forge header / footer modules.
 *
 * `'help_center'` is the canonical Forge page name (see
 * `JsmForgePageName`) used by the legacy `<ForgeElement />` for the same
 * header/footer slots. `noWrapper` + `allowCustomPadding` match the legacy
 * invocation so visual treatment is identical across legacy help-center
 * and the new hub editor.
 *
 * Returning `null` for unknown `elementType`s (rather than throwing) keeps
 * the editor render loop deterministic — the empty state owned by
 * `<HubForgeExtension />` is the user-visible fallback.
 */
const ForgeBridge: React.FC<Parameters<HubForgeRenderer>[0]> = ({ extensionNode, mode }) => {
	const elementType = extensionNode.parameters?.elementType;
	const isPageInEditMode = mode === ExtensionMode.EDITOR;
	const isFullWidthLayout = extensionNode.layout === 'full-width';
	const elementWidth = getElementWidth(extensionNode.layout);

	const isValid =
		elementType === ForgeElementType.HEADER_AND_SUB_HEADER_PANELS ||
		elementType === ForgeElementType.FOOTER_PANELS;

	React.useEffect(() => {
		if (!isValid) {
			trackError(
				'hub.forge.element.type.invalid',
				{ elementType },
				'Invalid Hub Forge Element Type received',
			);
		}
	}, [isValid, elementType]);

	if (!isValid) {
		return null;
	}

	const inner =
		elementType === ForgeElementType.HEADER_AND_SUB_HEADER_PANELS ? (
			<ForgeHeaderSlot isPageInEditMode={isPageInEditMode} />
		) : (
			<ForgeFooterSlot isPageInEditMode={isPageInEditMode} />
		);

	return (
		<div css={isFullWidthLayout && fullWidthRootCss}>
			<ElementRoot
				removePadding
				removeMargin={isIncreasedFlexibilityEnabled()}
				elementWidth={elementWidth}
			>
				<div
					data-hub-extension-bridge-content="forge"
					css={[fullWidthCss, isPageInEditMode && editModeCss]}
				>
					{inner}
				</div>
			</ElementRoot>
		</div>
	);
};

const renderHubForge: HubForgeRenderer = (props) => <ForgeBridge {...props} />;

// ---------------------------------------------------------------------------
// Connect bridge
// ---------------------------------------------------------------------------

/**
 * Connect panels are Redux-backed: `<ConnectFragment>` fetches them on mount
 * and the result lands in the store keyed by page + location. The selector
 * returns `undefined` until the fetch settles, then an array (possibly empty)
 * or an error. We treat "all requested locations resolved to an empty array"
 * as "no Connect app for this slot" and show the editor empty state. The
 * `<ConnectFragment>`s are still rendered so they keep ownership of fetching
 * and of rendering the real app when present.
 */
const connectLocationsHaveApp = (
	state: State,
	locations: ConnectFragmentLocation[],
): boolean | undefined => {
	// Gate on the fetch lifecycle, not on the panel values. A successful fetch
	// that returns no apps stores `undefined`/`[]` for individual locations,
	// which is indistinguishable from "not fetched yet" if we only look at the
	// panel values — that bug kept the empty state hidden forever. Using the
	// page-level resolution signal lets the empty state appear once the fetch
	// settles, even when there are no Connect apps.
	if (!isConnectFragmentsResolved({ state, pageName: HELP_CENTER })) {
		return undefined;
	}
	// Resolved: report whether any requested location actually has a panel.
	return locations.some((location) => {
		const panel = getConnectAddonPanels({ state, location, pageName: HELP_CENTER });
		return Array.isArray(panel) && panel.length > 0;
	});
};

/**
 * Adapter from the platform-level Connect renderer signature to the
 * legacy help-center `ConnectFragment` for the help-center page.
 *
 * For the header slot we render both `headerPanels` and `subheaderPanels`
 * to match the legacy `<ConnectElement />` behaviour — Connect apps that
 * registered against either location continue to render in the new hub
 * editor with zero migration work.
 */
const ConnectBridge: React.FC<Parameters<HubConnectRenderer>[0]> = ({ extensionNode, mode }) => {
	const elementType = extensionNode.parameters?.elementType;
	const isPageInEditMode = mode === ExtensionMode.EDITOR;
	const isFullWidthLayout = extensionNode.layout === 'full-width';
	const elementWidth = getElementWidth(extensionNode.layout);

	const isValid =
		elementType === ConnectElementType.HEADER_AND_SUB_HEADER_PANELS ||
		elementType === ConnectElementType.FOOTER_PANELS;

	React.useEffect(() => {
		if (!isValid) {
			trackError(
				'hub.connect.element.type.invalid',
				{ elementType },
				'Invalid Hub Connect Element Type received',
			);
		}
	}, [isValid, elementType]);

	if (!isValid) {
		return null;
	}

	const locations =
		elementType === ConnectElementType.HEADER_AND_SUB_HEADER_PANELS
			? (['headerPanels', 'subheaderPanels'] as const)
			: (['footerPanels'] as const);

	return (
		<ConnectSlot
			isPageInEditMode={isPageInEditMode}
			locations={[...locations]}
			elementWidth={elementWidth}
			isFullWidthLayout={isFullWidthLayout}
		/>
	);
};

/**
 * Renders the Connect fragments for the given locations in the published
 * renderer. In the editor's detached React root we render the placeholder
 * directly, avoiding redux-backed ConnectFragment components there.
 */
interface ConnectSlotOwnProps {
	isPageInEditMode: boolean;
	locations: ConnectFragmentLocation[];
	elementWidth?: ElementWidth;
	isFullWidthLayout: boolean;
}

interface ConnectSlotStateProps {
	hasApp: boolean | undefined;
}

const ConnectPublishedSlotInner: React.FC<ConnectSlotOwnProps & ConnectSlotStateProps> = ({
	locations,
	hasApp,
	elementWidth,
	isFullWidthLayout,
}) => {
	if (hasApp === false) {
		return null;
	}

	return (
		<div css={isFullWidthLayout && fullWidthRootCss}>
			<ElementRoot
				removePadding
				removeMargin={isIncreasedFlexibilityEnabled()}
				elementWidth={elementWidth}
			>
				<div data-hub-extension-bridge-content="connect" css={fullWidthCss}>
					{locations.map((location) => (
						<ConnectFragment key={location} page={HELP_CENTER} location={location} />
					))}
				</div>
			</ElementRoot>
		</div>
	);
};

const ConnectPublishedSlot = connect(
	(state: State, ownProps: ConnectSlotOwnProps): ConnectSlotStateProps => ({
		hasApp: connectLocationsHaveApp(state, ownProps.locations),
	}),
)(ConnectPublishedSlotInner);

const ConnectEditorSlot: React.FC<
	Pick<ConnectSlotOwnProps, 'elementWidth' | 'isFullWidthLayout'>
> = ({ elementWidth, isFullWidthLayout }) => (
	<div css={isFullWidthLayout && fullWidthRootCss}>
		<ElementRoot
			removePadding
			removeMargin={isIncreasedFlexibilityEnabled()}
			elementWidth={elementWidth}
		>
			<div data-hub-extension-bridge-content="connect" css={[fullWidthCss, editModeCss]}>
				<HubConnectEmptyState />
			</div>
		</ElementRoot>
	</div>
);

const ConnectSlot: React.FC<ConnectSlotOwnProps> = ({
	isPageInEditMode,
	locations,
	elementWidth,
	isFullWidthLayout,
}) =>
	isPageInEditMode ? (
		<ConnectEditorSlot elementWidth={elementWidth} isFullWidthLayout={isFullWidthLayout} />
	) : (
		<ConnectPublishedSlot
			isPageInEditMode={false}
			locations={locations}
			elementWidth={elementWidth}
			isFullWidthLayout={isFullWidthLayout}
		/>
	);

const renderHubConnect: HubConnectRenderer = (props) => <ConnectBridge {...props} />;

// ---------------------------------------------------------------------------
// Internal provider wrapper
// ---------------------------------------------------------------------------

interface HubExtensionsBridgeProps {
	children: ReactNode;
}

/**
 * Single mount point that injects both Hub Forge and Hub Connect renderers.
 * Order of providers is irrelevant — they are independent contexts.
 */
const HubExtensionsBridge = ({ children }: HubExtensionsBridgeProps): React.JSX.Element => (
	<HubForgeRendererProvider renderer={renderHubForge}>
		<HubConnectRendererProvider renderer={renderHubConnect}>{children}</HubConnectRendererProvider>
	</HubForgeRendererProvider>
);

/**
 * Provider passed to the platform hub packages as
 * `productExtensionsDependenciesProvider` (see `HubAppProps`).
 *
 * The hub editor renders extension nodes as ReactNodeViews in its own
 * detached React root, so the route-level `<HubExtensionsBridge>` context
 * does not reach them. The platform wraps every extension node — in both
 * the published renderer and the editor — with this single provider, so it
 * must establish BOTH the base hub extension dependencies (via
 * `CommonHubExtensionsDependenciesProvider`, the default the platform uses
 * when no override is supplied) AND the Forge / Connect renderer bridge.
 */
export const HubExtensionsDependenciesProvider = ({
	children,
}: {
	children?: ReactNode;
}): React.JSX.Element => (
	<IntlProvider locale="en">
		<CommonHubExtensionsDependenciesProvider>
			<HubExtensionsBridge>{children}</HubExtensionsBridge>
		</CommonHubExtensionsDependenciesProvider>
	</IntlProvider>
);

// ---------------------------------------------------------------------------
// Styles (declared at the bottom of the file per styled-component-order)
// ---------------------------------------------------------------------------

const getElementWidth = (layout: Parameters<HubForgeRenderer>[0]['extensionNode']['layout']) =>
	layout === 'full-width' ? ElementWidth.FULL_WIDTH : undefined;

const fullWidthRootCss = css({
	alignSelf: 'stretch',
	width: 'auto',
});

const fullWidthCss = css({ width: '100%' });
const editModeCss = css({ pointerEvents: 'none' });
